import React, { ForwardRefRenderFunction, FormEvent, useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import { WithTranslation, TranslationProps } from '../../contexts/TranslationContext';
import { fetchCountries } from '../../service/address.api';
import { Pays } from '../../interfaces-base';
import SelectCountry from './components/SelectCountry';
import FormAddressItem from './components/FormAddressItem';
import RadioAddressType from './components/RadioAddressType';
import { BaseForm, ProForm, CustomerType, FormAddressRef, AddressType, FormKeys, FetchInfos } from './fa-interfaces';
import { getShopRefInStorage } from '../mon-compte/MonCompteAction';
import SectionPro from './components/SectionPro';
import FormAddressInput from './components/FormAddressInput';
import { getAddressFromId, getDefaultBaseForm, getDefaultProForm, findCountry, getSubmitState, countryHasRequiredSiret, countryIsAvailableForShipping } from './utils/form.util';
import { getFieldError, getFieldsError } from './utils/validator.util';
import FormAddressInputPhone from './components/FormAddressInputPhone';
import SectionLocation from './components/SectionLocation';
import '../../conf-color-app.css';
import './style-form-address.css';

interface Props {
  type: AddressType;
  addressId?: number;
  disabledCheckboxShipping: boolean;
  showFavoriteCheckbox?: boolean;
  onLoad?: () => void;
}

const DEFAULT_FETCH_INFOS_VALUE: FetchInfos = {
  savedResponse: undefined,
  needToFetch: true,
  loading: false
};

const defaultErrorList: Partial<Record<FormKeys, undefined | string>> = {
  telephone_mobile: undefined,
  telephone_fixe: undefined
};

const defaultFetchState: Partial<Record<FormKeys, FetchInfos>> = {
  code_postal: { ...DEFAULT_FETCH_INFOS_VALUE },
  telephone_mobile: { ...DEFAULT_FETCH_INFOS_VALUE },
  telephone_fixe: { ...DEFAULT_FETCH_INFOS_VALUE }
};

const FormAddressApp: ForwardRefRenderFunction<FormAddressRef, Props & TranslationProps> = ({ textsContent, addressId, type, disabledCheckboxShipping, showFavoriteCheckbox, onLoad }, ref): JSX.Element => {
  const [formState, setFormState] = useState<BaseForm>(getDefaultBaseForm());
  const [customerType, setCustomerType] = useState<CustomerType>('private');
  const [proState, setProState] = useState<ProForm>(getDefaultProForm());
  const [countriesList, setCountriesList] = useState<Pays[]>([]);
  const [errorList, setErrorList] = useState<Partial<Record<FormKeys, undefined | string>>>({ ...defaultErrorList });
  const [fetchState, setFetchState] = useState<Partial<Record<FormKeys, FetchInfos | undefined>>>({ ...defaultFetchState });
  const [tvaFetchState, setTvaFetchState] = useState<FetchInfos>({ ...DEFAULT_FETCH_INFOS_VALUE });
  const [formWidth, setFormWidth] = useState<number>(1000);

  const refForm = useRef<HTMLDivElement>(null);
  const refPhoneCountries = useRef<Record<'telephone_fixe' | 'telephone_mobile', Pays|undefined>>({
    'telephone_fixe': undefined,
    'telephone_mobile': undefined
  });

  useEffect(() => {
    fetchCountries()
      .then(response => setCountriesList(response.pays));
  }, []);

  useEffect(() => {
    const fnResize = () => {
      if (refForm.current) {
        setFormWidth(refForm.current.clientWidth);
      }
    };
    fnResize();
    window.addEventListener('resize', fnResize);
    return () => window.removeEventListener('resize', fnResize);
  }, [refForm.current]);

  useEffect(() => {
    setFormState(previousState => ({
      ...previousState,
      is_facturation: type === 'billing' ? 1 : 0,
      is_livraison: type === 'shipping' || !disabledCheckboxShipping || !addressId ? 1 : 0
    }));
  }, [type, disabledCheckboxShipping]);

  useEffect(() => {
    if (countriesList.length > 0 && addressId) {
      getAddressFromId(addressId)
        .then(async ([baseForm, proForm]): Promise<void> => {
          const isPro = proForm.societe !== null;
          const country = findCountry(countriesList, baseForm.pays_id);

          if (isPro && countryHasRequiredSiret(country, type) && proForm.siret === null) {
            proForm.societe = null;
          }

          setFormState(baseForm);
          setProState(proForm);
          isPro && setCustomerType('pro');
          
          const errors = await getFieldsError(
            type,
            isPro ? 'pro' : 'private',
            country,
            { ...baseForm, ...proForm },
            { ...fetchState, numero_tva: { ...tvaFetchState } },
            updateFetchState
          );
          setErrorList(errors);
        })
        .finally(() => onLoad?.());
    } else if (countriesList.length > 0) {
      setFormState(prev => ({
        ...getDefaultBaseForm(),
        is_facturation: prev.is_facturation,
        is_livraison: prev.is_livraison
      }));
      setProState(getDefaultProForm());
      setErrorList({ ...defaultErrorList });
      setFetchState({ ...defaultFetchState });
      setTvaFetchState({ ...DEFAULT_FETCH_INFOS_VALUE });
      refPhoneCountries.current = {
        'telephone_fixe': undefined,
        'telephone_mobile': undefined
      };

      onLoad?.();
    }
  }, [countriesList, addressId]);

  const updateFetchState = (key: FormKeys) => (loading: boolean, needToFetch: boolean, savedResponse: any | undefined) => {
    if (key === 'numero_tva') {
      setTvaFetchState(prev => ({
        ...prev,
        loading,
        needToFetch,
        savedResponse
      }));
    } else if (fetchState[key]) {
      setFetchState(prev => ({
        ...prev,
        [key]: {
          loading,
          needToFetch,
          savedResponse
        }
      }));
    }
  }

  useImperativeHandle(ref, () => ({
    formIsValid: async (): Promise<boolean> => {
      let check = true;
      const errors = await getFieldsError(
        type,
        customerType,
        findCountry(countriesList, formState.pays_id),
        { ...formState, ...proState },
        { ...fetchState, numero_tva: { ...tvaFetchState } }, updateFetchState);
      const values = Object.values(errors);
      let i = 0;
      while (i < values.length && check) {
        check = values[i] === undefined;
        i++;
      }

      setErrorList(errors);
      return check;
    },
    getState: () => getSubmitState(customerType, formState, proState)
  }));

  const recheckErroredFields = async (newBaseState: BaseForm, newProState: ProForm): Promise<void> => {
    const newResult = { ...errorList };
    const keys = Object.keys(errorList) as FormKeys[];
    for (const key of keys) {
      if (errorList[key] !== undefined || (['code_postal', 'telephone_mobile', 'telephone_fixe'].includes(key) && { ...newBaseState, ...newProState }[key] !== undefined)) {
        const fetchKey = fetchState?.[key];
        newResult[key]  = await getFieldError(
          key,
          { ...newBaseState, ...newProState }[key]?.toString(),
          type,
          customerType,
          findCountry(countriesList, newBaseState.pays_id),
          { ...newBaseState, ...newProState },
          { state: (fetchKey ? { ...fetchKey, needToFetch: true } : undefined), updateState: updateFetchState(key) }
        );
      }
    }
    setErrorList(newResult);
  }

  const getDetectedPhoneCountry = (key: FormKeys): Pays|undefined => {
    return countriesList.find(item => item.codePays === fetchState?.[key]?.savedResponse?.phone_number_validate?.pays_phone_number);
  };

  const handleBaseFormChange = async (key: keyof BaseForm, value?: string, customCountryId: number|undefined = undefined): Promise<void> => {
    return new Promise((resolve) => {
      setFormState((previousBaseForm) => {
        const newState = { ...previousBaseForm, [key]: value };
        const keyFetchState = fetchState?.[key];
        updateFetchState(key)(keyFetchState?.loading ?? false, true, undefined);
        getFieldError(
          key,
          value,
          type,
          customerType,
          findCountry(countriesList, customCountryId ?? formState.pays_id),
          { ...newState, ...proState , [key]: value},
          { state: (keyFetchState ? { ...keyFetchState, needToFetch: true, savedResponse: undefined } : undefined), updateState: updateFetchState(key) }
        ).then(newError => {
          setErrorList(previousState => ({
            ...previousState,
            [key]: newError
          }));
        }).finally(() => resolve());

        return newState;
      });
    })
  };

  const defaultHandleChange = (key: keyof BaseForm) => (event: FormEvent<HTMLInputElement>): void => {
    const { target } = event;
    if (target instanceof HTMLInputElement) {
      handleBaseFormChange(key, target.value);
    }
  };

  const handlePhoneChange = (key: 'telephone_fixe' | 'telephone_mobile') => async (value: string, country: Pays): Promise<void> => {
    refPhoneCountries.current[key] = country;
    await handleBaseFormChange(key, value, country.idPays);
    const newKey = key === 'telephone_fixe' ? 'telephone_mobile' : 'telephone_fixe';
    await handleBaseFormChange(newKey, formState[newKey], refPhoneCountries.current[newKey]?.idPays);
  };

  const handleFieldProChange = async (key: keyof ProForm, value: string): Promise<void> => {
    setProState((previousState) => ({
      ...previousState,
      [key]: value
    }));
    setTvaFetchState(prev => ({
      ...prev,
      needToFetch: true,
      savedResponse: undefined
    }));
    const keyFetchState = fetchState?.[key];
    const newError = await getFieldError(
      key,
      value,
      type,
      customerType,
      findCountry(countriesList, formState.pays_id),
      { ...formState, ...proState, [key]: value },
      { state: (keyFetchState ? { ...keyFetchState, needToFetch: true } : undefined), updateState: updateFetchState(key) }
    );
    setErrorList(previousState => ({
      ...previousState,
      [key]: newError
    }));
  };

  const handleCountryChange = (value: Pays): void => {
    const newBaseForm = { ...formState, telephone_code_pays: value.codePays, pays_id: value.idPays };
    const newProState = { ...getDefaultProForm(), societe: proState.societe };

    if (type === 'billing' && newBaseForm.is_livraison === 1 && !countryIsAvailableForShipping(value, customerType === 'pro')) {
      newBaseForm.is_livraison = 0;
    }

    setFormState(newBaseForm);
    setProState(newProState);
    recheckErroredFields(newBaseForm, newProState);
  };

  const handleCheckboxChange = (key: 'is_livraison' | 'is_favorite') => (event: FormEvent<HTMLInputElement>): void => {
    const { target } = event;
    if (target instanceof HTMLInputElement) {
      setFormState(previous => ({
        ...previous,
        [key]: target.checked ? 1 : 0
      }));
    }
  };

  const countrySelected = findCountry(countriesList, formState.pays_id);
  const filteredCountry = countriesList.filter(item => type === 'billing' || countryIsAvailableForShipping(item, customerType === 'pro'));
  const modeReduce = formWidth <= 550;

  const errors = { ...errorList };
  for (const key of Object.keys(errors) as FormKeys[]) {
    if (errors[key] !== undefined) {
      errors[key] = textsContent?.[errors[key] ?? ''] ?? errors[key];
    }
  }

  return (
    <div className={`tunnel-app fa-form-app ${getShopRefInStorage()} ${modeReduce && 'reduce'}`} ref={refForm}>
      <FormAddressItem label={textsContent?.['SHIPPING_FORM_COUNTRY']}>
        <SelectCountry textsContent={textsContent} items={filteredCountry} value={formState.pays_id} onChange={handleCountryChange} />
      </FormAddressItem>
      <RadioAddressType textsContent={textsContent} value={customerType} addressType={type} onChange={(v) => setCustomerType(v)} />
      {countrySelected && customerType === 'pro' && (
        <SectionPro
          textsContent={textsContent}
          addressType={type}
          state={proState}
          country={countrySelected}
          errorList={errors}
          tvaFetchState={tvaFetchState}
          modeReduce={modeReduce}
          zipCode={formState.code_postal}
          onChange={handleFieldProChange}
        />
      )}
      <div className="fa-fullname-group">
        <FormAddressInput
          label={textsContent?.['SHIPPING_FORM_FIRSTNAME']}
          value={formState.prenom}
          name="prenom"
          errorMessage={errors['prenom']}
          onChange={defaultHandleChange('prenom')}
        />
        <FormAddressInput
          label={textsContent?.['SHIPPING_FORM_NAME']}
          value={formState.nom}
          name="nom"
          errorMessage={errors['nom']}
          onChange={defaultHandleChange('nom')}
        />
      </div>
      <SectionLocation
        textsContent={textsContent}
        state={formState}
        country={countrySelected}
        errorList={errors}
        zipFetchState={fetchState['code_postal']}
        onChange={handleBaseFormChange}
      />
      <div className="fa-grid">
        <FormAddressInputPhone
          phoneType="mobile"
          countries={countriesList}
          currentCountry={countrySelected}
          detectCountry={getDetectedPhoneCountry('telephone_mobile')}
          label={textsContent?.['SHIPPING_FORM_MOBILE']}
          errorMessage={errors['telephone_mobile']}
          loading={fetchState['telephone_mobile']?.loading}
          value={formState.telephone_mobile}
          name="telephone_mobile"
          onChange={handlePhoneChange('telephone_mobile')}
        />
        <FormAddressInputPhone
          phoneType="fixe"
          countries={countriesList}
          currentCountry={countrySelected}
          detectCountry={getDetectedPhoneCountry('telephone_fixe')}
          label={textsContent?.['SHIPPING_FORM_TEL']}
          errorMessage={errors['telephone_fixe']}
          loading={fetchState['telephone_fixe']?.loading}
          value={formState.telephone_fixe}
          name="telephone_fixe"
          onChange={handlePhoneChange('telephone_fixe')}
        />
        <FormAddressInput
          label={textsContent?.['SHIPPING_FORM_LABEL']}
          value={formState.libelle}
          name="libelle"
          onChange={defaultHandleChange('libelle')}
        />
        {type === 'billing' && !disabledCheckboxShipping && countryIsAvailableForShipping(countrySelected, customerType === 'pro') && !addressId && (
          <div className="fa-checkbox-container">
            <FormAddressInput id="fa-checkbox-same" type="checkbox" checked={formState.is_livraison === 1} onChange={handleCheckboxChange('is_livraison')} />
            <label htmlFor="fa-checkbox-same">{textsContent?.['SHIPPING_CHECKBOX_USE_SAME_ADDRESS_V2']}</label>
          </div>
        )}
        {showFavoriteCheckbox && (
          <div className="fa-checkbox-container">
            <FormAddressInput id="fa-checkbox-favorite" type="checkbox" checked={formState.is_favorite === 1} onChange={handleCheckboxChange('is_favorite')} />
            <label htmlFor="fa-checkbox-favorite">
              {textsContent?.[`SHIPPING_FORM_FAVORITE_CHECKBOX_${type.toUpperCase()}`]}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithTranslation<Props & TranslationProps, FormAddressRef>(forwardRef(FormAddressApp), 'SHIPPING_TUNNEL', 'EXCEPTIONS');
